import React from 'react';
import { Box, Flex } from '@withjoy/joykit';
import type { ThemeJson } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { useColorPaletteProvider } from '../../../../ColorPaletteProvider/ColorPaletteProvider';
// eslint-disable-next-line no-restricted-imports -- imports lazy wrapper from admin
import { LazyLoadedMonogramView } from '@apps/admin/routes/WebsiteDesignerV2/routes/Layout/components/HeaderPhotoCustomization/components/MonogramEditorDialog/MonogramView.lazy';
// eslint-disable-next-line no-restricted-imports -- generated GraphQL query hook only; no admin dependencies
import { useJsonDataContainerByIdQuery } from '@website-custom-components/JsonWrapper.graphql.generated';
import { useMonogramPersonalizationOverrides } from '../hooks/useMonogramPersonalizationOverrides';
// 432 = 6 inches × 72 PPI (points per inch, standard screen resolution constant)
export const MONOGRAM_SIZE = 432;

type MonogramViewRendererProps = Readonly<{
  config?: ThemeJson;
  showPlaceholder?: boolean;
  previewInitials?: ReadonlyArray<string>;
  previewEventDate?: number | null;
}>;

/**
 * Pure monogram renderer — draws a monogram from already-fetched JSON content.
 * Shared by the standalone `monogram` header layout and the combined
 * photo/video + monogram layouts.
 */
export const MonogramViewRenderer = ({ config, showPlaceholder, previewInitials, previewEventDate }: MonogramViewRendererProps) => {
  const { pageColor, pageFontColor } = useColorPaletteProvider();

  if (!config) {
    return showPlaceholder ? (
      <Flex
        justifyContent="center"
        alignItems="center"
        width="100%"
      >
        <Box
          width={MONOGRAM_SIZE}
          height={MONOGRAM_SIZE}
          backgroundColor="lightgray"
        />
      </Flex>
    ) : null;
  }

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      width="100%"
    >
      <LazyLoadedMonogramView
        json={config}
        background={pageColor}
        fontColor={pageFontColor}
        initials={previewInitials}
        eventDate={previewEventDate}
      />
    </Flex>
  );
};

type MonogramByContainerIdProps = Readonly<{
  jsonContainerId: string | null | undefined;
  showPlaceholder?: boolean;
}>;

/**
 * Fetches the monogram JSON container by id and renders it. Encapsulates the
 * GraphQL fetch + personalization overrides so any background layout can drop
 * in a monogram with just a `jsonContainerId`.
 */
export const MonogramByContainerId = ({ jsonContainerId, showPlaceholder = false }: MonogramByContainerIdProps) => {
  const { data } = useJsonDataContainerByIdQuery({
    variables: {
      payload: {
        id: jsonContainerId || '',
      },
    },
    batchMode: 'fast',
    skip: !jsonContainerId,
  });

  const jsonContent = data?.jsonDataContainerById?.content;

  const { initials: previewInitials, eventDate: previewEventDate } = useMonogramPersonalizationOverrides();

  return (
    <MonogramViewRenderer
      config={jsonContent}
      showPlaceholder={showPlaceholder}
      previewInitials={previewInitials}
      previewEventDate={previewEventDate}
    />
  );
};
